// ---------------------------------------------------------------------------
// TEDH Usage Context — Builds the `X-TEDH-Usage-Context` request header.
//
// This is the single place the webapp constructs the TEDH usage-context header.
// Instrumented `DataHubService` methods call `usageContextRequestOptions(...)`
// to attach a labelled usage context to the exact TEDH calls that represent a
// deliberate user action (see the Datasets usage-tracking requirements doc,
// section 8 — "Usage definition & webapp instrumentation rules").
//
// Design notes for future devs:
//   - The builder is intentionally *lenient*: it never throws. If serialization
//     fails or the action is empty, it returns no header rather than breaking
//     the underlying dataset operation. This mirrors TEDH's own contract, where
//     a malformed usage header never fails the call.
//   - `action` is authoritative per instrumented method (callers cannot change
//     it), so the recorded usage label is always correct. Callers only supply
//     `sourceId` / `extras`.
//   - Values are defensively sanitized to the contract's caps (action length,
//     extras entry count) so we never send an over-limit header.
// ---------------------------------------------------------------------------

import type { RequestOptions } from '../api/client.js';
import { config } from '../config.js';

import type {
  TedhUsageContext,
  UsageContextOverrides,
} from './usageContext.interface.js';

export type { TedhUsageContext, UsageContextOverrides };

/** HTTP header name defined by the TEDH usage-context contract. */
export const USAGE_CONTEXT_HEADER_NAME = 'X-TEDH-Usage-Context';

/** Contract limit: `action` label is capped at 256 characters. */
export const MAX_ACTION_LENGTH = 256;

/** Contract limit: `extras` may carry at most 16 entries. */
export const MAX_EXTRAS_ENTRIES = 16;

/**
 * Canonical action labels for the dataset surfaces instrumented from the
 * Datasets SPA. Keep these stable — the usage report groups events by this
 * exact string. Add new labels here (never inline literals) as later PRs
 * instrument more surfaces.
 */
export const DATASET_USAGE_ACTIONS = {
  CREATE_DATASET: 'Create Dataset',
  UPDATE_DATASET: 'Update Dataset',
  CREATE_DRAFT: 'Create Draft',
  DELETE_DATASET: 'Delete Dataset',
  DELETE_PARTICIPANT_DATA: 'Delete Participant Data',
  SAVE_ATTRIBUTES: 'Save Dataset Attributes',
  CREATE_DATA_ENTRY: 'Create Data Entry',
  UPDATE_DATA_ENTRY: 'Update Data Entry',
  DELETE_DATA_ENTRY: 'Delete Data Entry',
  PUBLISH_VERSION: 'Publish Version',
  UPLOAD_DATASET: 'Upload Data (folder)',
  DOWNLOAD_DATASET: 'Download Dataset',
  UPLOAD_FILE: 'Upload Data Entry File',
  DELETE_DATA_ENTRY_FILE: 'Delete Data Entry File',
  CREATE_ANNOTATION: 'Create Annotation',
  UPDATE_ANNOTATION: 'Update Annotation',
  DELETE_ANNOTATION: 'Delete Annotation',
  CREATE_ANNOTATION_SET: 'Create Annotation Set',
  UPDATE_ANNOTATION_SET: 'Update Annotation Set',
  PUBLISH_ANNOTATION_SET: 'Publish Annotation Set',
  DELETE_ANNOTATION_SET: 'Delete Annotation Set',
  DELETE_ANNOTATION_FILE: 'Delete Annotation File',
  UPLOAD_ANNOTATION_FILE: 'Upload Annotation File',
  DOWNLOAD_ANNOTATION_FILE: 'Download Annotation File',
  UPLOAD_ANNOTATIONS: 'Upload Annotations (folder)',
  DOWNLOAD_ANNOTATIONS: 'Download Annotations',
} as const;

export const TEST_SET_USAGE_ACTIONS = {
  CREATE: 'Create Test Set',
  PUBLISH: 'Publish Test Set',
  CREATE_DRAFT: 'Create Test Set Draft',
  DELETE: 'Delete Test Set',
  ADD_DATA: 'Add Test Set Data',
  DELETE_DATA: 'Delete Test Set Data',
} as const;

export const ACCESS_CONTROL_USAGE_ACTIONS = {
  SAVE_DATASET_PERMISSIONS: 'Save Dataset Permissions',
  SAVE_TENANT_PERMISSIONS: 'Save Tenant Permissions',
} as const;

/** Union of the known TEDH usage action labels. */
export type TedhUsageAction =
  | (typeof DATASET_USAGE_ACTIONS)[keyof typeof DATASET_USAGE_ACTIONS]
  | (typeof TEST_SET_USAGE_ACTIONS)[keyof typeof TEST_SET_USAGE_ACTIONS]
  | (typeof ACCESS_CONTROL_USAGE_ACTIONS)[keyof typeof ACCESS_CONTROL_USAGE_ACTIONS];

/**
 * Canonical `extras` keys attached to usage events. Centralized so producers
 * (service callers) and any future consumer of the usage report agree on the
 * exact key names. `extras` values are always strings per the TEDH contract.
 */
export const USAGE_EXTRA_KEYS = {
  /** Number of files involved (folder upload of dataset or annotations). */
  FILE_COUNT: 'fileCount',
  /**
   * Number of data entries involved (folder upload / dataset download /
   * annotation bulk upload / annotation download).
   */
  ENTRY_COUNT: 'entryCount',
  /** Human-readable dataset name, for report readability. */
  DATASET_NAME: 'datasetName',
  /**
   * Frontend scenario correlation GUID (the operational scenario `queryId`).
   * Carried to TEDH so the Layer 1 usage record joins the Layer 2 Aria
   * scenario in Kusto. Set only when a real (non-empty) id is available.
   */
  CORRELATION_ID: 'correlationId',
} as const;

/**
 * Build a usage `extras` map carrying the scenario correlation GUID, merged
 * with any caller-supplied extras. The correlation entry is included only when
 * `correlationId` is a non-empty string, so a disabled telemetry logger (which
 * yields an empty `queryId`) never puts a meaningless `correlationId: ''` on
 * the wire. Returns `undefined` when there is nothing to send.
 */
export function withCorrelationId(
  correlationId: string | undefined,
  extras?: Record<string, string>,
): Record<string, string> | undefined {
  const hasCorrelation =
    typeof correlationId === 'string' && correlationId.length > 0;
  if (!hasCorrelation) {
    return extras;
  }
  return { ...extras, [USAGE_EXTRA_KEYS.CORRELATION_ID]: correlationId };
}

/**
 * Sanitize `extras` to the contract: at most {@link MAX_EXTRAS_ENTRIES} entries,
 * with string keys and string values. Returns `undefined` when there is nothing
 * usable so the serialized header stays minimal.
 */
function sanitizeExtras(
  extras: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (extras == null) {
    return undefined;
  }

  const sanitized: Record<string, string> = {};
  let count = 0;
  for (const [key, value] of Object.entries(extras)) {
    if (count >= MAX_EXTRAS_ENTRIES) {
      break;
    }
    if (key.length === 0) {
      continue;
    }
    // Coerce defensively — the type says string, but runtime data may not be.
    sanitized[key] = String(value);
    count += 1;
  }

  return count > 0 ? sanitized : undefined;
}

/**
 * Serialize a {@link TedhUsageContext} into the header value string, applying
 * the contract's caps. Returns `undefined` when the context has no usable
 * `action`, signalling the caller to omit the header entirely.
 */
export function serializeUsageContext(
  context: TedhUsageContext,
): string | undefined {
  const action = context.action.trim().slice(0, MAX_ACTION_LENGTH);
  if (action.length === 0) {
    return undefined;
  }

  // Build with a stable key order (action, sourceId, extras) and omit optional
  // fields when empty, so the header stays compact and deterministic.
  const payload: TedhUsageContext = { action };

  const sourceId = context.sourceId?.trim();
  if (sourceId != null && sourceId.length > 0) {
    payload.sourceId = sourceId;
  }

  const extras = sanitizeExtras(context.extras);
  if (extras != null) {
    payload.extras = extras;
  }

  try {
    return JSON.stringify(payload);
  } catch {
    // Lenient by contract: never let usage instrumentation break the call.
    return undefined;
  }
}

/**
 * Build the `X-TEDH-Usage-Context` header map for a usage context. Returns an
 * empty object (no header) when:
 *   - the usage-context header is disabled for this environment
 *     (`config.tedhUsageHeaderEnabled` is false), or
 *   - the context cannot be serialized.
 *
 * The result can always be safely spread into request headers.
 */
export function buildUsageContextHeader(
  context: TedhUsageContext,
): Record<string, string> {
  if (!config.tedhUsageHeaderEnabled) {
    return {};
  }
  const value = serializeUsageContext(context);
  return value == null ? {} : { [USAGE_CONTEXT_HEADER_NAME]: value };
}

/**
 * Build {@link RequestOptions} carrying the usage-context header, ready to pass
 * to an `IHttpClient` write call. The `action` is fixed by the instrumented
 * method; callers may add `sourceId` / `extras` via {@link UsageContextOverrides}.
 *
 * The header is emitted only in environments where it is enabled
 * (`config.tedhUsageHeaderEnabled`). When enabled, the builder is lenient and
 * omits the header only when the context cannot be serialized.
 */
export function usageContextRequestOptions(
  action: TedhUsageAction,
  overrides?: UsageContextOverrides,
): RequestOptions {
  return {
    headers: buildUsageContextHeader({ action, ...overrides }),
  };
}

/**
 * Opt-in variant of {@link usageContextRequestOptions} for service methods that
 * must NOT record a usage event on every call. It returns `undefined` (i.e. no
 * request options at all) unless the caller explicitly supplies `usage`.
 *
 * Two categories of method need this, both introduced with the dataset
 * upload/download surfaces:
 *
 *   1. **Multi-call flows** — a folder upload or a full-dataset download issues
 *      the same TEDH call many times (once per batch of entries). Per the
 *      requirements doc (decision D2), each such flow must emit exactly ONE
 *      summary usage event, not one per batch. The orchestrator therefore
 *      passes `usage` to a single representative call and omits it on the rest.
 *
 *   2. **Shared methods** — e.g. `batchAddDataEntries` is used both by folder
 *      upload (a tracked action) and by synthetic-data generation (not a
 *      tracked dataset action). Making the header opt-in lets the folder-upload
 *      caller tag its call while the synthetic-gen caller stays silent, without
 *      the service needing to know which caller it is.
 *
 * When `usage` IS supplied, the header is built via
 * {@link usageContextRequestOptions}.
 */
export function optionalUsageContextRequestOptions(
  action: TedhUsageAction,
  usage: UsageContextOverrides | undefined,
): RequestOptions | undefined {
  return usage == null ? undefined : usageContextRequestOptions(action, usage);
}
